import { RedditReport } from 'wb-reddit/app/routes/reports/components/report';
import {
	FC, useMemo, useState,
} from 'react';
import {
	AvatarSize,
	BackArrowIcon,
	BanIcon,
	Button,
	ButtonAppearance,
	ButtonColor,
	CloseCircleIcon, AsyncComboboxAutocomplete, Dialog,
	Highlight,
	Label,
	Menu,
	NetworkConnectionIcon,
	handleErrorNotification,
} from '@aleph/shared-components';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
	RedditUsersAndGroupsDocument, RedditUsersAndGroupsQueryFullEmailUsers, RedditUsersAndGroupsQueryItems,
	RedditUsersAndGroupsQueryItemsItemsItems,
	UpdateRedditReportAccessDocument,
} from 'wb-reddit/graphql/generated';
import {
	isEqual, orderBy, uniq, uniqBy,
} from 'lodash';
import { AnimatePresence, motion } from 'framer-motion';
import RedditEntityImage from 'wb-reddit/app/common/components/entityImage';
import debounce from 'debounce-promise';
import { Trans, useTranslation } from 'react-i18next';
import { captureException } from '@sentry/react';

type Entity = RedditUsersAndGroupsQueryFullEmailUsers |
RedditUsersAndGroupsQueryItems |
RedditUsersAndGroupsQueryItemsItemsItems;

interface RedditReportSharingModalProps {
	report: RedditReport;
	onClose: () => void;
}

function notEmpty<TValue>(value: TValue | null | undefined): value is TValue {
	return value !== null && value !== undefined;
}

const RedditReportSharingModal: FC<RedditReportSharingModalProps> = ({ report, onClose }) => {
	const { t } = useTranslation();

	const [selectedEntities, setSelectedEntities] = useState<Entity[]>([
		...(report.users ?? []),
		...(report.userGroups ?? []),
	]);

	const [setReportAccess, { loading: saving }] = useMutation(UpdateRedditReportAccessDocument);

	const [getUsersAndGroups] = useLazyQuery(
		RedditUsersAndGroupsDocument,
	);

	const debouncedGetUsersAndGroups = useMemo(() => debounce(getUsersAndGroups, 300), []);

	const loadOptions = async (search: string) => {
		if (search.length < 3) {
			return [];
		}

		const { data, error } = await debouncedGetUsersAndGroups({
			variables: {
				usersFilterEmail: {
					userEmail: [{
						value:    [search ? `*${search}*` : ''],
						wildcard: true,
					}],
					userId: [{
						value: selectedEntities.reduce((accu, entity) => {
							if (entity.__typename === 'SearchUser' || entity.__typename === 'User') {
								accu.push(entity.id);
							}

							return accu;
						}, [] as string[]),
						exclude: true,
					}],
				},
				usersFilterName: {
					userFullName: [{
						value:    [search ? `*${search}*` : ''],
						wildcard: true,
					}],
					userId: [{
						value: selectedEntities.reduce((accu, entity) => {
							if (entity.__typename === 'SearchUser' || entity.__typename === 'User') {
								accu.push(entity.id);
							}

							return accu;
						}, [] as string[]),
						exclude: true,
					}],
				},
				userGroupFilter: {
					userGroupId: [{
						value: selectedEntities.reduce((accu, entity) => {
							if (entity.__typename === 'UserGroup') {
								accu.push(entity.id);
							}

							return accu;
						}, [] as string[]),
						exclude: true,
					}],
				},
				userGroupSearchFilter: {
					userGroupName: [{
						value:    [search ? `*${search}*` : ''],
						wildcard: true,
					}],
					userGroupId: [{
						value: selectedEntities.reduce((accu, entity) => {
							if (entity.__typename === 'UserGroup') {
								accu.push(entity.id);
							}

							return accu;
						}, [] as string[]),
						exclude: true,
					}],
				},
				searchUserFilter: {
					userEmail: [{
						value: search,
					}],
				},
			},
		});

		if (error) {
			captureException(error);

			handleErrorNotification(
				error,
				t(
					're: reports sharing autocomplete error',
					'Something went wrong while searching for sharing options',
				),
			);

			return [];
		}

		return uniqBy([
			...(data?.fullEmailUsers ?? []),
			...((data?.sameOrganizationUsersName.items ?? [])),
			...((data?.sameOrganizationUsersEmail.items ?? [])),
			...(data?.sharedTeamsName.items ?? []).flatMap(item => item.users).map(item => item?.user),
			...(data?.sharedTeamsEmail.items ?? []).flatMap(item => item.users).map(item => item?.user),
			...(data?.sharedTeams?.items ?? []),
		].filter(notEmpty), (entity) => entity.id);
	};

	const onSelectEntity = (entity: Entity) => {
		setSelectedEntities(curr => [...curr, entity]);
	};

	const saveReportSharing = async () => {
		const isUserEntity = (entity: Entity) => entity.__typename === 'SearchUser'
			|| entity.__typename === 'User';

		await setReportAccess({
			variables: {
				reportId: report?.id ?? '',
				userIds:  uniq(selectedEntities
					.filter((
						entity,
					): entity is RedditUsersAndGroupsQueryItems => isUserEntity(entity))
					.map(entity => entity.id)),
				userGroupIds: uniq(selectedEntities
					.filter((
						entity,
					): entity is RedditUsersAndGroupsQueryItemsItemsItems => !isUserEntity(entity))
					.map(entity => entity.id)),
				userEmails: uniq(selectedEntities.reduce((accu, entity) => {
					if (isUserEntity(entity)) {
						accu = [...accu, (entity as RedditUsersAndGroupsQueryItems).userEmail];
					}

					if (!isUserEntity(entity)) {
						accu = [...accu, ...((entity as RedditUsersAndGroupsQueryItemsItemsItems).users ?? [])
							.map(user => user?.user?.userEmail)];
					}

					return accu;
				}, [] as string[])),
			},
		});

		onClose();
	};

	return (
		<Dialog
			onClose={onClose}
			isOpen
			className="min-h-[50vh] max-w-[600px]"
			data-testid="share-report-container"
		>
			<Dialog.Header
				preset={
					{
						prefix: <NetworkConnectionIcon />,
						title:  t('re: reports.share', 'Share this report'),
					}
				}
			/>
			<Dialog.Content>
				<AsyncComboboxAutocomplete
					inputProps={{
						placeholder: t('re: add users or teams', 'Add users or teams...'),
					}}
					size='large'
					loadOptions={loadOptions}
					renderOption={({ option, searchQuery, onSelected }) => (
						<Menu.Item
							key={option.value}
							className="h-auto"
							onClick={(e) => {
								e.stopPropagation();
								onSelectEntity(option);
								onSelected();
							}}
						>
							<RedditEntityImage
								size={AvatarSize.ExtraSmall}
								entity={option}
							/>
							<Label>
								<Highlight
									source={option.label ?? ''}
									target={searchQuery.trim()}
								>
									{s => <strong>{s}</strong>}
								</Highlight>
							</Label>
							{
								option.__typename === 'SearchUser' || option.__typename === 'User'
									? <p className='ml-3 text-[10px] text-gray-600'>
										@{option?.userEmail?.split('@')?.[1]}
									</p> : null
							}
						</Menu.Item>
					)}
					NoOptionsComponent={({ search }) => {
						if ((search?.length ?? 0) < 3) {
							return <Menu.Item>{t('sc: input length', 'Input at least 3 characters')}</Menu.Item>;
						}

						return (<div className='max-w-[25vw] w-fit'>
							{
								<Trans
									i18nKey="sc: no match"
									defaults="<strong>No match.</strong> Please enter a full email address to add people outside your organization."
								>
									<strong>No match.</strong>
									Please enter a full email address to add people outside your organization.
								</Trans>
							}
						</div>);
					}}
				/>
				<div className="flex items-center justify-between h-[32px] px-[8px] text-[11px] text-gray-600 py-[8px]">
					<div>
						{t('re: report sharing modal contributor', 'CONTRIBUTOR')}
					</div>

					<div>
						{t('re: report sharing modal remove', 'REMOVE')}
					</div>
				</div>
				<div className="flex-1 overflow-y-auto">
					<AnimatePresence>
						{
							orderBy(selectedEntities, entity => (isEqual(entity, report.owner) ? -1 : 1)).map((entity) => (
								<motion.div
									key={`${entity?.id}_${entity?.__typename}`}
									initial={{ height: '48px' }}
									exit={{ height: 0 }}
									transition={{ duration: 0.1 }}
									className="flex overflow-hidden items-center w-full rounded h-[48px] px-[8px] gap-[8px]
									hover:bg-gray-100 border-b border-b-gray-200"
								>
									<RedditEntityImage entity={entity} />
									<div className="flex-1 flex items-center gap-[8px] overflow-hidden">
										<div className="pointer-events-none text-[16px] text-gray-950 overflow-hidden
										overflow-ellipsis whitespace-nowrap"
										>
											{entity.label}
										</div>
									</div>
									{
										isEqual(entity, report.owner)
											? (
												<div className="text-[11px] leading-[22px]
										text-gray-800"
												>
													{t('re: report sharing modal owner', 'Owner')}
												</div>
											)
											: <Button
												leftNode={<CloseCircleIcon />}
												appearance={ButtonAppearance.GHOST}
												color={ButtonColor.GRAY}
												onClick={
													() => setSelectedEntities(
														curr => curr
															.filter(selectedEntity => selectedEntity.id !== entity.id),
													)
												}
											/>
									}
								</motion.div>
							))
						}
					</AnimatePresence>
				</div>
			</Dialog.Content>
			<Dialog.Footer>
				<Button
					title={t('sc: discard changes', 'Discard changes')}
					leftNode={<BanIcon />}
					appearance={ButtonAppearance.GHOST}
					color={ButtonColor.RED}
					onClick={onClose}
					data-testid="discard-sharing-changes"
				/>
				<Button
					title={t('sc: confirm', 'Confirm')}
					leftNode={<BackArrowIcon />}
					loading={saving}
					disabled={selectedEntities.length === 0 || saving}
					onClick={saveReportSharing}
					data-testid="save-sharing-changes"
				/>
			</Dialog.Footer>
		</Dialog>
	);
};

export default RedditReportSharingModal;
